import {
    InstalledBaseNodeSubscriptionInput,
    InstalledBaseRegionSubscriptionInput,
    IotAlertSeverity,
    IotMaintenanceType,
} from "../codegen/graphql";

/**
 * Form state shared by node and region subscriptions (both input types carry
 * exactly the same channel / IoT alert / maintenance-due fields).
 */
export interface SubscriptionInputValues {
    mail: boolean;
    push: boolean;
    iotAlert: boolean;
    iotAlertSeverity: IotAlertSeverity;
    maintenanceDue: boolean;
    maintenanceSeverity: IotAlertSeverity;
    /** `null` = notify for every maintenance kind. */
    maintenanceType: IotMaintenanceType | null;
    /**
     * SLA status change opt-in. `undefined` (non-admins, SLA flag off) leaves
     * the key out so the backend keeps the stored value.
     */
    slaStatusChange?: boolean;
}

export type SubscriptionInput = InstalledBaseNodeSubscriptionInput &
    InstalledBaseRegionSubscriptionInput;

/**
 * Builds the partial-update payload for create/update of a node or region
 * subscription.
 *
 * The backend keeps every omitted field unchanged, which the payload relies
 * on: while an opt-in is switched off its severity/type keys are left out
 * so the previously chosen values survive an off/on round trip. While
 * maintenance-due is on, `maintenanceType` is always sent — including an
 * explicit `null`, which is the only way to reset a kind restriction back to
 * "all kinds" (omitting the key would keep the old restriction).
 */
export const buildSubscriptionInput = (
    values: SubscriptionInputValues
): SubscriptionInput => ({
    // Web App channel is mandatory and always active.
    inApp: true,
    mail: values.mail,
    push: values.push,
    iotAlert: values.iotAlert,
    ...(values.iotAlert ? { iotAlertSeverity: values.iotAlertSeverity } : {}),
    maintenanceDue: values.maintenanceDue,
    ...(values.maintenanceDue
        ? {
              maintenanceSeverity: values.maintenanceSeverity,
              maintenanceType: values.maintenanceType,
          }
        : {}),
    ...(values.slaStatusChange !== undefined
        ? { slaStatusChange: values.slaStatusChange }
        : {}),
});
